import {
    Box,
    Card,
    CardContent,
    Typography,
    Paper,
    Container,
    Badge,
    CircularProgress,
    Button,
    Tooltip
} from '@mui/material';
import {
    Person,
    CalendarToday,
    WhatsApp,
    ArrowForward,
} from '@mui/icons-material';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import { alpha } from '@mui/material/styles';

import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getDiasFromFiltro } from '../../utils/date';

const MUIKanbanBoard = ({
    dateFilter,
    startDate,
    endDate,
    setForceTokenExpired,
    handleWhatsAppIdClick,
    setTotalLeads,
    leads: externalLeads,
}) => {
    const [leads, setLeads] = useState(externalLeads || []);
    const [isLoading, setIsLoading] = useState(false);
    const [copiedLeadId, setCopiedLeadId] = useState(null);
    const copiedTimeoutRef = useRef(null);

    const [visibleLeadsPerColumn, setVisibleLeadsPerColumn] = useState({});
    const [loadingAllColumns, setLoadingAllColumns] = useState({});
    const [cadenciasExpanded, setCadenciasExpanded] = useState(false);

    const copyToClipboard = async (text) => {
        if (!text) return false;
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                return true;
            } catch {
                return false;
            }
        }
    };

    const handleWhatsAppContextMenu = async (event, lead) => {
        event.preventDefault();
        event.stopPropagation();
        const success = await copyToClipboard(lead?.whatsAppId);
        if (!success) return;

        setCopiedLeadId(lead?.idLead);
        if (copiedTimeoutRef.current) {
            clearTimeout(copiedTimeoutRef.current);
        }
        copiedTimeoutRef.current = setTimeout(() => {
            setCopiedLeadId(null);
        }, 1200);
    };

    useEffect(() => {
        return () => {
            if (copiedTimeoutRef.current) {
                clearTimeout(copiedTimeoutRef.current);
            }
        };
    }, []);

    console.log(leads)

    // Função para determinar o status do lead
    const getLeadStatus = (lead) => {
        const idSituacao = lead.idSituacaoInterna;
        const tentativas = lead.tentativasContato || 0;

        // Mapeamento direto do idSituacaoInterna para as colunas
        switch (idSituacao) {
            case 0:
                return 'em-qualificacao'; // Em atendimento
            case 1:
                return 'qualificado'; // Qualificado
            case 2:
                return 'descartado-conversa'; // Descartado - Conversa
            case 3:
                return 'descartado-cadencia'; // Descartado - Cadência
            case 4:
                return 'nao-whatsapp'; // Não é WhatsApp
            case 5:
                return 'Retirado-da-etapa-IA'; // Retirado da etapa IA
            case 8:
                return 'outros-sistemas'; // Outros Sistemas
            case 6:
                return 'primeiro-contato'; // Novo Lead
            case 7:
                // Cadência - verificar número de tentativas
                return tentativas >= 5
                    ? `cadencia-5`
                    : `cadencia-${tentativas}`;
            default:
                return 'outro'; // fallback para valores não mapeados
        }
    };

    // Remova o initialLeads e useState, use:
    const leadsWithStatus = leads.map(lead => ({
        ...lead,
        status: getLeadStatus(lead),
        // Mapear campos do backend para o formato esperado
        name: lead.nome || lead.nomeCompleto || 'Nome não informado',
        email: lead.email || 'Email não informado',
        phone: lead.telefone || lead.whatsAppId || 'Telefone não informado',
        company: lead.empresa || 'Empresa não informada',
        source: lead.origem || 'Origem não informada',
        date: lead.dataHoraCadastroAtualizado ?
            new Date(lead.dataHoraCadastroAtualizado).toLocaleDateString('pt-BR') :
            new Date().toLocaleDateString('pt-BR')
    }));

    const totalLeads = leadsWithStatus.length;

    const fontSizeTitleCard = "18px";

    const columns = useMemo(() => [
        {
            id: 'primeiro-contato',
            title: 'Novo Lead',
            color: '#9c27b0',   // roxo
            bgColor: '#f3e5f5',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'cadencia-1',
            title: '1ª Cadência',
            color: '#ff5722',   // vermelho-laranja
            bgColor: '#fbe9e7',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'cadencia-2',
            title: '2ª Cadência',
            color: '#ff9800',   // laranja
            bgColor: '#fff3e0',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'cadencia-3',
            title: '3ª Cadência',
            color: '#ffc107',   // amarelo-laranja
            bgColor: '#fffde7',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'cadencia-4',
            title: '4ª Cadência',
            color: '#ffeb3b',   // amarelo
            bgColor: '#f9fbe7',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'cadencia-5',
            title: '5ª Cadência',
            color: '#cddc39',   // lime
            bgColor: '#f0f4c3',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'em-qualificacao',
            title: 'Em atendimento',
            color: '#43a047',
            bgColor: '#e8f5e9',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'qualificado',
            title: 'Qualificado',
            color: '#8e24aa',
            bgColor: '#f3e5f5',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'descartado-conversa',
            title: 'Descartado - Conversa',
            color: '#e53935',
            bgColor: '#ffebee',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'descartado-cadencia',
            title: 'Descartado - Cadência',
            color: '#e53935',
            bgColor: '#ffebee',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'nao-whatsapp',
            title: 'Não é WhatsApp',
            color: '#00897b',
            bgColor: '#e0f2f1',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'Retirado-da-etapa-IA',
            title: 'Retirado da etapa IA',
            color: '#607d8b',   // azul-acinzentado
            bgColor: '#eceff1',
            fontSize: fontSizeTitleCard
        },
        {
            id: 'outros-sistemas',
            title: 'Outros Sistemas',
            color: '#3949ab',   // indigo
            bgColor: '#e8eaf6',
            fontSize: fontSizeTitleCard
        },
    ], [fontSizeTitleCard]);

    useEffect(() => {
        const initialVisible = {};
        columns.forEach(column => {
            initialVisible[column.id] = 12;
        });
        setVisibleLeadsPerColumn(initialVisible);
    }, [columns]);

    useEffect(() => {
        if (externalLeads && Array.isArray(externalLeads)) {
            setLeads(externalLeads);
        }
    }, [externalLeads]);


    // URL base da API
    const baseURL = process.env.REACT_APP_API_BASE_URL;

    // useEffect para buscar os dados quando as datas mudarem
    useEffect(() => {
        // Se os leads vierem de fora (Gestão), não faz fetch
        if (externalLeads && Array.isArray(externalLeads)) {
            return;
        }

        const fetchLeadsData = async () => {
            try {
                setIsLoading(true);

                const token = localStorage.getItem('jwtToken');
                if (!token) {
                    console.error("Token não encontrado no localStorage.");
                    return;
                }

                const decodedToken = jwtDecode(token);
                const idClienteAipim = decodedToken.id_cliente;
                const dias = getDiasFromFiltro(dateFilter);

                const params = {
                    idCliente: idClienteAipim,
                    dataInicio: startDate,
                    dataFim: endDate,
                    dias
                };

                const response = await axios.get(`${baseURL}/produto/sdr/leads-cliente`, {
                    params,
                    headers: { Authorization: `Bearer ${token}` }
                });

                const sortedData = response.data.sort((a, b) => {
                    return new Date(b.dataHoraCadastroAtualizado) - new Date(a.dataHoraCadastroAtualizado);
                });

                setLeads(sortedData);
            } catch (error) {
                console.error("Erro ao buscar dados dos leads:", error);
                if (error.response?.status === 401 ||
                    error.response?.data?.message?.toLowerCase().includes("token")) {
                    setForceTokenExpired(true);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchLeadsData();
    }, [dateFilter, startDate, endDate, baseURL, setForceTokenExpired, externalLeads]);

    useEffect(() => {
        if (typeof setTotalLeads === 'function') {
            setTotalLeads(totalLeads);
        }
    }, [totalLeads, setTotalLeads]);


    // Adicione um loading state no início do return
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Função para lidar com "Ver todos" com loading
    const handleViewAllColumn = async (columnId, totalLeads) => {
        setLoadingAllColumns(prev => ({ ...prev, [columnId]: true }));
        // Simula carregamento
        await new Promise(resolve => setTimeout(resolve, 1500));
        setVisibleLeadsPerColumn(prev => ({ ...prev, [columnId]: totalLeads }));
        setLoadingAllColumns(prev => ({ ...prev, [columnId]: false }));
    };

    // Função para ver mais leads
    const handleViewMoreColumn = (columnId, currentVisible) => {
        setVisibleLeadsPerColumn(prev => ({
            ...prev,
            [columnId]: currentVisible + 20
        }));
    };

    // Função para ver menos leads
    const handleViewLessColumn = (columnId) => {
        setVisibleLeadsPerColumn(prev => ({
            ...prev,
            [columnId]: 12
        }));
    };



    const LeadCard = ({ lead }) => {
        const [expanded, setExpanded] = useState(false);
        const [rotation,] = useState(0);
        const [imageLoaded, setImageLoaded] = useState(false)
        const hasWhatsApp = Boolean(lead.whatsAppId) && lead.status !== 'nao-whatsapp';

        // Função para formatar data
        const formatDate = (dateString) => {
            if (!dateString) return null;
            try {
                const date = new Date(dateString);

                // Garante dois dígitos para dia, mês, hora e minuto
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();

                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');

                return `${day}/${month}/${year} - ${hours}h:${minutes}m`;
            } catch {
                return dateString;
            }
        };

        const handleToggleExpand = (e) => {
            e.stopPropagation();
            setExpanded(!expanded);
        };

        //administrar

        const handleCvToggle = (e, lead) => {
            const leadUrlCVBase = lead.cvUrlBase || ""

            // Abre uma nova aba com a URL desejada
            window.open(`${leadUrlCVBase}gestor/comercial/leads/${lead.idLead}/administrar`, '_blank');
        };

        console.log(leads)

        return (
            <Card
                sx={{
                    mb: 2,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                        boxShadow: 4
                    },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                }}
                elevation={2}
            >
                <CardContent sx={{ pb: '16px !important' }}>
                    {/* Header sempre visível - Nome e Número */}
                    <Box display="flex" alignItems="flex-start" mb={1}>
                        <Box flex={1}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: hasWhatsApp ? 'space-between' : 'flex-end',
                                    gap: hasWhatsApp ? 0 : 1,
                                }}
                            >
                                {lead.nome && (
                                    <Typography
                                        variant="h6"
                                        component="h3"
                                        fontSize="0.85rem"
                                        fontWeight="600"
                                        sx={{ wordBreak: 'break-word', lineHeight: 1.3, whiteSpace: 'pre-line' }}
                                    >
                                        {lead.nome
                                            .split(/\s+/)
                                            .filter(p => !['de', 'da', 'do', 'dos', 'das', 'e'].includes(p.toLowerCase())) // remove preposições
                                            .slice(0, 2) // pega no máximo dois nomes
                                            .join('\n')} {/* adiciona quebra de linha entre eles */}
                                    </Typography>
                                )}

                                {/* Botão de expandir */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {lead.dataHoraCadastroAtualizado && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.7rem' }}
                                        >
                                            {formatDate(lead.dataHoraCadastroAtualizado).split(' ')[0]}
                                        </Typography>
                                    )}
                                    <Box
                                        onClick={handleToggleExpand}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark'
                                            },
                                            transform: expanded ? 'rotate(270deg)' : 'rotate(90deg)',
                                            transition: 'transform 0.3s ease'
                                        }}
                                    >
                                        <ArrowForward sx={{ fontSize: 14, fontWeight: 'bold' }} />
                                        </Box>
                                </Box>
                            </Box>

                            {/* WhatsApp/Telefone sempre visível */}
                            {hasWhatsApp && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, justifyContent: 'flex-start' }}>

                                    <WhatsApp sx={{ fontSize: 14, mr: 0.5, color: '#25D366' }} />

                                    {/* Botão clicável seguindo o padrão da tabela */}
                                    <Tooltip
                                        title="Copiado"
                                        open={copiedLeadId === lead.idLead}
                                        placement="top"
                                        arrow
                                        disableFocusListener
                                        disableHoverListener
                                        disableTouchListener
                                    >
                                        <Box
                                            onClick={(e) => {
                                                handleWhatsAppIdClick(lead); // Usar a função integrada
                                            }}
                                            onContextMenu={(event) => handleWhatsAppContextMenu(event, lead)}
                                            sx={{
                                                cursor: 'pointer',
                                                display: 'inline-block',
                                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                color: 'primary.main',
                                                px: 1,
                                                py: 0.3,
                                                borderRadius: 2,
                                                fontSize: '0.7rem',
                                                fontWeight: '500',
                                                border: '1px solid rgba(102, 126, 234, 0.2)',
                                                transition: 'all 0.2s ease-in-out',
                                                maxWidth: '100px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                '&:hover': {
                                                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                },
                                            }}
                                        >
                                            {lead.whatsAppId}
                                        </Box>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>


                    </Box>

                    {/* Conteúdo expandido */}
                    {expanded && (
                        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            {/* ID Lead */}
                            {lead.idLead && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Person sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="caption">
                                        ID Lead: {lead.idLead}
                                    </Typography>
                                </Box>
                            )}

                            {/* Tentativas de contato */}
                            {(lead.tentativasContato !== undefined && lead.tentativasContato !== null) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <WorkHistoryIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="caption">
                                        Tentativas de Cadência: {lead.tentativasContato}
                                    </Typography>
                                </Box>
                            )}

                            {/* Data de cadastro atualizado */}
                            {lead.dataHoraCadastroAtualizado && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <CalendarToday sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="caption">
                                        Cadastro: {formatDate(lead.dataHoraCadastroAtualizado)}
                                    </Typography>
                                </Box>
                            )}

                            {/* Chips na parte inferior */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>


                                {/* Imagem redonda clicável */}
                                {lead.idCRM === 1 && (
                                    <Tooltip title="Ver status do Lead no CV" arrow>
                                        <Box
                                            onClick={(e) => handleCvToggle(e, lead)}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                transform: `rotate(${rotation}deg)`,
                                                transition: 'transform 0.6s ease-in-out',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: imageLoaded ? 'transparent' : 'action.hover',
                                                '&:hover': {
                                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                                },
                                            }}
                                        >
                                            {!imageLoaded && (
                                                <CircularProgress
                                                    size={16}
                                                    sx={{
                                                        position: 'absolute',
                                                        color: '#667eea'
                                                    }}
                                                />
                                            )}
                                            <img
                                                src="/CV_Logo.png"
                                                alt="CV Icon"
                                                onLoad={() => setImageLoaded(true)}
                                                onError={() => setImageLoaded(true)}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    opacity: imageLoaded ? 1 : 0,
                                                    transition: 'opacity 0.3s ease',
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>

            {/* Board */}
            <Paper
                ref={(el) => {
                    if (el) {
                        let isDown = false;
                        let startX;
                        let scrollLeft;

                        el.addEventListener('mousedown', (e) => {
                            isDown = true;
                            el.style.cursor = 'grabbing';
                            startX = e.pageX - el.offsetLeft;
                            scrollLeft = el.scrollLeft;
                            e.preventDefault(); // Adicione esta linha
                        });

                        el.addEventListener('mouseleave', () => {
                            isDown = false;
                            el.style.cursor = 'grab';
                        });

                        el.addEventListener('mouseup', () => {
                            isDown = false;
                            el.style.cursor = 'grab';
                        });

                        el.addEventListener('mousemove', (e) => {
                            if (!isDown) return;
                            e.preventDefault();
                            const x = e.pageX - el.offsetLeft;
                            const walk = (x - startX) * 2;
                            el.scrollLeft = scrollLeft - walk;
                        });
                    }
                }}
                sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'scroll',
                    pb: 2,
                    minHeight: '70vh',
                    cursor: 'grab',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    // Evita seleção de texto
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',

                    // Remover completamente borda e fundo
                    border: 'none',
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                }}
            >
                {columns.map(column => {
                    // Se for cadência e não está expandido, pular (exceto a primeira)
                    if (column.id.includes('cadencia') && !cadenciasExpanded && column.id !== 'cadencia-1' && column.id !== 'descartado-cadencia') {
                        return null;
                    }

                    // Se for a primeira cadência e não está expandido, agregar todos os leads de cadência
                    const columnLeads = column.id === 'cadencia-1' && !cadenciasExpanded
                        ? leadsWithStatus.filter(lead => lead.status?.startsWith('cadencia-'))
                        : leadsWithStatus.filter(lead => lead.status === column.id);

                    // Ajustar o título da coluna quando colapsada
                    const columnTitle = column.id === 'cadencia-1' && !cadenciasExpanded
                        ? 'Cadências'
                        : column.title;

                    return (
                        <Paper
                            key={column.id}
                            elevation={2}
                            sx={{
                                minWidth: 215,
                                maxWidth: 215,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? alpha(column.color, 0.16)
                                        : column.bgColor,
                                color: 'text.primary',
                                border: '1px solid',
                                borderColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? alpha(column.color, 0.28)
                                        : 'transparent',
                                p: 2,
                                opacity: (column.id.includes('cadencia') && columnLeads.length === 0) ? 0.5 : 1,
                                filter: (column.id.includes('cadencia') && columnLeads.length === 0) ? 'grayscale(1)' : 'none',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {/* Column Header */}
                            <Box mb={2}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography fontSize={fontSizeTitleCard} fontWeight="600" sx={{ color: 'text.primary' }}>
                                        {columnTitle}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Tooltip
                                            title={columnLeads.length > 99 ? columnLeads.length : ''}
                                            disableHoverListener={columnLeads.length <= 99}
                                        >
                                            <Badge
                                                badgeContent={columnLeads.length}
                                                max={99} // vai mostrar "99+" no badge, mas o tooltip mostra o valor real
                                                sx={{
                                                    '& .MuiBadge-badge': {
                                                        backgroundColor: column.color,
                                                        color: 'white',
                                                        fontWeight: columnLeads.length <= 99 ? '600' : '300',
                                                        fontSize: columnLeads.length <= 99 ? 'normal' : 11.25,
                                                        width: '100%',
                                                    },
                                                }}
                                            />
                                        </Tooltip>


                                        {column.id === 'cadencia-1' && (
                                            <ArrowCircleRightIcon
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCadenciasExpanded(!cadenciasExpanded);
                                                }}
                                                sx={{
                                                    fontSize: 24,
                                                    color: column.color,
                                                    cursor: 'pointer',
                                                    transform: cadenciasExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.3s ease',
                                                    ml: 1,
                                                    '&:hover': {
                                                        opacity: 0.7
                                                    }
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>

                                {/* Barra de progresso e porcentagem */}
                                <Box sx={{ mt: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: column.color,
                                                fontWeight: '600',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {totalLeads > 0 ? ((columnLeads.length / totalLeads) * 100).toFixed(1) : 0}%
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 4,
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255,255,255,0.14)'
                                                    : 'rgba(0,0,0,0.1)',
                                            borderRadius: 2,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: `${totalLeads > 0 ? (columnLeads.length / totalLeads) * 100 : 0}%`,
                                                height: '100%',
                                                backgroundColor: column.color,
                                                borderRadius: 2,
                                                transition: 'width 0.3s ease-in-out'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Cards */}
                            <Box sx={{
                                maxHeight: '60vh',
                                overflowY: 'auto',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                '&::-webkit-scrollbar': {
                                    display: 'none'
                                },
                            }}>
                                {loadingAllColumns[column.id] ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "200px",
                                            py: 4,
                                            gap: 2,
                                        }}
                                    >
                                        <CircularProgress size={30} />
                                        <Typography sx={{ color: "text.secondary", textAlign: "center", fontSize: '0.8rem' }}>
                                            Carregando todos os leads...
                                        </Typography>
                                    </Box>
                                ) : columnLeads.length > 0 ? (
                                    <>
                                        <Box sx={{height: '100%'}}>
                                            {columnLeads.slice(0, visibleLeadsPerColumn[column.id] || 12).map(lead => (
                                                <LeadCard key={lead.id} lead={lead} />
                                            ))}
                                        </Box>

                                        {/* Botões de paginação */}
                                        {columnLeads.length > 12 && (
                                            <Box sx={{ textAlign: 'center', p: 1.5, borderTop: '1px solid', borderColor: 'divider', mt: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>

                                                    {/* Botão "Ver mais" */}
                                                    {(visibleLeadsPerColumn[column.id] || 12) < columnLeads.length && (
                                                        <Button
                                                            onClick={() => handleViewMoreColumn(column.id, visibleLeadsPerColumn[column.id] || 12)}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                px: 1.5, py: 0.5,
                                                                border: '1px solid #667eea',
                                                                borderRadius: '12px',
                                                                background: 'rgba(102, 126, 234, 0.08)',
                                                                color: '#667eea',
                                                                fontWeight: 500,
                                                                fontSize: '0.7rem',
                                                                textTransform: 'none',
                                                                minWidth: '60px',
                                                                minHeight: '24px',
                                                                '&:hover': {
                                                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
                                                                    background: 'rgba(102, 126, 234, 0.12)',
                                                                }
                                                            }}
                                                        >
                                                            Ver mais
                                                        </Button>
                                                    )}

                                                    {/* Botão "Ver menos" */}
                                                    {(visibleLeadsPerColumn[column.id] || 12) > 12 && (
                                                        <Button
                                                            onClick={() => handleViewLessColumn(column.id)}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                px: 1.5, py: 0.5,
                                                                border: '1px solid #e53935',
                                                                borderRadius: '12px',
                                                                background: 'rgba(229, 57, 53, 0.08)',
                                                                color: '#e53935',
                                                                fontWeight: 500,
                                                                fontSize: '0.7rem',
                                                                textTransform: 'none',
                                                                minWidth: '60px',
                                                                minHeight: '24px',
                                                                '&:hover': {
                                                                    boxShadow: '0 2px 8px rgba(229, 57, 53, 0.2)',
                                                                    background: 'rgba(229, 57, 53, 0.12)',
                                                                }
                                                            }}
                                                        >
                                                            Ver menos
                                                        </Button>
                                                    )}

                                                    {/* Botão "Ver todos" */}
                                                    {(visibleLeadsPerColumn[column.id] || 12) < columnLeads.length && (
                                                        <Button
                                                            onClick={() => handleViewAllColumn(column.id, columnLeads.length)}
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={loadingAllColumns[column.id]}
                                                            sx={{
                                                                px: 1.5, py: 0.5,
                                                                border: '1px solid #43a047',
                                                                borderRadius: '12px',
                                                                background: 'rgba(76, 175, 80, 0.08)',
                                                                color: '#43a047',
                                                                fontWeight: 500,
                                                                fontSize: '0.7rem',
                                                                textTransform: 'none',
                                                                minWidth: '60px',
                                                                minHeight: '24px',
                                                                '&:hover': {
                                                                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
                                                                    background: 'rgba(76, 175, 80, 0.12)',
                                                                },
                                                                '&:disabled': {
                                                                    opacity: 0.6,
                                                                }
                                                            }}
                                                        >
                                                            {loadingAllColumns[column.id] ? 'Carregando...' : 'Ver todos'}
                                                        </Button>
                                                    )}
                                                </Box>

                                                {/* Indicador de quantidade */}
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        mt: 0.5,
                                                        display: 'block',
                                                        fontSize: '0.65rem'
                                                    }}
                                                >
                                                    Mostrando {Math.min(visibleLeadsPerColumn[column.id] || 12, columnLeads.length)} de {columnLeads.length}
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <Box textAlign="center" py={4}>
                                        <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhum lead nesta etapa
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    );
                })}
            </Paper>
        </Container>
    );
}

export default MUIKanbanBoard;
